import { AUTH_SUCCESS_MESSAGES } from '@parlay-picker/shared/authMessages';
import type { ReactElement } from 'react';

interface RecoveryData {
  email: string;
  deletedAt: string;
  permanentDeletionDate: string;
  oauthProvider?: string;
  oauthIdToken?: string;
}

interface AccountRecoveryModalProps {
  recoveryData: RecoveryData;
  password: string;
  rememberMe: boolean;
  recoveryLoading: boolean;
  recoveryError: string;
  onRecoveryLoadingChange: (value: boolean) => void;
  onRecoveryErrorChange: (value: string) => void;
  onRecoveryDataClear: () => void;
  onNavigateHome: () => void;
  onLogin: (email: string, password: string, rememberMe: boolean) => Promise<unknown>;
  onShowToast: (message: string, type: 'success' | 'error') => void;
}

export function AccountRecoveryModal({
  recoveryData,
  password,
  rememberMe,
  recoveryLoading,
  recoveryError,
  onRecoveryLoadingChange,
  onRecoveryErrorChange,
  onRecoveryDataClear,
  onNavigateHome,
  onLogin,
  onShowToast,
}: AccountRecoveryModalProps): ReactElement {
  const handleRecover = async (): Promise<void> => {
    onRecoveryLoadingChange(true);
    onRecoveryErrorChange('');
    try {
      const body: Record<string, string> = { email: recoveryData.email };
      if (recoveryData.oauthProvider && recoveryData.oauthIdToken) {
        body.provider = recoveryData.oauthProvider;
        body.idToken = recoveryData.oauthIdToken;
      } else {
        body.password = password;
      }

      const res = await fetch('/api/auth/account/recover', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error || 'Recovery failed');
      }

      onRecoveryDataClear();
      onShowToast(AUTH_SUCCESS_MESSAGES.recovered.toast, 'success');
      if (recoveryData.oauthProvider) {
        window.location.reload();
      } else {
        await onLogin(recoveryData.email, password, rememberMe);
        onNavigateHome();
      }
    } catch (err) {
      onRecoveryErrorChange((err as Error).message || 'Recovery failed');
    } finally {
      onRecoveryLoadingChange(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-theme-surface rounded-2xl p-6 border border-theme-border w-full max-w-sm shadow-xl">
        <h3 className="text-lg font-semibold text-white mb-2">Account Scheduled for Deletion</h3>
        <p className="text-sm text-theme-text-secondary mb-4">
          Your account ({recoveryData.email}) is scheduled for permanent deletion
          {recoveryData.permanentDeletionDate ? (
            <>
              {' '}
              on{' '}
              <span className="text-white font-medium">
                {new Date(recoveryData.permanentDeletionDate).toLocaleDateString()}
              </span>
            </>
          ) : null}
          . Would you like to recover it?
        </p>

        {recoveryError && <p className="text-sm text-red-400 mb-3">{recoveryError}</p>}

        <div className="flex gap-2">
          <button
            type="button"
            disabled={recoveryLoading}
            onClick={handleRecover}
            className="flex-1 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {recoveryLoading ? 'Recovering...' : 'Recover Account'}
          </button>
          <button
            type="button"
            onClick={() => {
              onRecoveryDataClear();
              onRecoveryErrorChange('');
            }}
            className="px-4 py-2 bg-theme-bg hover:bg-theme-border text-theme-text-secondary font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
